import { ApiError } from "./api";
import { PRINTING_NOT_OPEN_NOTHING_TO_DO } from "./printing-open";

const internalLanguage =
  /\b(?:canonical|artifact|immutable|projection|principal|lineage|schema|checksum|digest|preflight|provider|payload|sandbox|deployment|reconciliation|idempotency|revision|allowance|allocation|budget|ceiling|reservation|quota|anthropic|openai|elevenlabs|claude|sonnet|opus|haiku|gpt)\b|\$\d|\bDPI\b|\bexact prompt\b|bedtime-[a-z]+(?:-[a-z]+)*-\d|Too Many Requests|[A-Z]{2,}_[A-Z_]{2,}|ZodError|invalid_type|Unexpected token|Failed to fetch|Load failed|NetworkError|stack trace|https?:\/\//iu;

/**
 * The book's picture-and-writing allowance is spent (services/src/book-variants/registry.ts and
 * the studio's story jobs, both 429). It may lift once in-flight work releases its reservation
 * (My Story chapters), so the words say both: wait for running work, otherwise ask us (2026-11-01).
 */
const ALLOWANCE_EXHAUSTED_CODES: ReadonlySet<string> = new Set([
  "BOOK_VARIANT_BUDGET_EXHAUSTED",
  "STORY_ALLOWANCE_EXHAUSTED",
  // The bedtime picture styles and optional pictures ("show me others") have the same honest
  // answer (Stage 2 review P2-17): services/src/domain/generation-budget.ts and handlers/story.ts.
  "STORY_VISUAL_ALLOWANCE_EXHAUSTED",
  "STORY_VISUAL_OWNER_CEILING",
  "SHOWCASE_OPTIONAL_BUDGET_EXHAUSTED",
]);
export const ALLOWANCE_EXHAUSTED_MESSAGE =
  "This book’s picture-and-writing allowance is used up for now. If something is still being made, wait for it to finish and try again; otherwise contact us and we’ll help.";

/** A definite refusal: the book's allowance is spent, and another try would be refused too. */
export function allowanceExhausted(error: unknown) {
  return error instanceof ApiError && ALLOWANCE_EXHAUSTED_CODES.has(error.code ?? "");
}

/** Preserve useful human validation; keep implementation details out of the interface. */
export function friendlyRequestError(error: unknown, fallback: string): string {
  if (allowanceExhausted(error)) return ALLOWANCE_EXHAUSTED_MESSAGE;
  let message = error instanceof Error ? error.message.trim() : "";
  if (!message || message.length > 360 || internalLanguage.test(message)) message = fallback;
  if (error instanceof ApiError) {
    if (error.status === 401)
      message = "Your sign-in needs refreshing. Sign in again, then reopen your book.";
    else if (error.status >= 500) message = fallback;
    // A reference helps our team only where the real reason is kept out of view.
    if (error.correlationId && message === fallback)
      message += ` Support reference: ${error.correlationId}.`;
  }
  return message;
}

/**
 * For the first load of a book's page: a refusal there means the book isn't shared with the
 * signed-in account (a family helper who lacks one permission is refused later, on an action).
 */
export function friendlyBookLoadError(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.status === 403 && error.code === "FORBIDDEN")
    return "This book belongs to a different account. Sign out, then sign in with the email that made the book.";
  return friendlyRequestError(error, fallback);
}

const PRINT_NOT_PREPARABLE =
  "This book can’t be prepared for printing from here yet. Your approval is saved, and nothing was sent to print. Please ask us for help.";
const PRINT_BOOK_CHANGED =
  "Your book changed after this review. Reload this page and review the current book before preparing it for print.";
/** The service's own words, kept: they are already written for the customer. */
const SERVICE_WORDS = "SERVICE_WORDS";
/**
 * Print preparation's refusals that asking again can never change while the book stays as it is,
 * in the customer's words (Stage 2, A2/A4). Only these stop a page's asking and hide its retry;
 * anything else, a conflict or a blip included, stays retryable (review P2-13, 2026-11-02).
 */
const PERMANENT_PRINT_PREPARATION_REFUSALS: Readonly<Record<string, string>> = {
  // Preparation is switched off: W3's words for printing that isn't open (printing-open.ts).
  CANONICAL_PRINT_DISPATCH_UNAVAILABLE: `${PRINTING_NOT_OPEN_NOTHING_TO_DO} Your approval is saved.`,
  CANONICAL_PRINT_TEMPLATE_EVIDENCE_REQUIRED: PRINT_NOT_PREPARABLE,
  CANONICAL_PRINT_BUCKET_REQUIRED: PRINT_NOT_PREPARABLE,
  BOOK_TEMPLATE_NOT_AVAILABLE: PRINT_NOT_PREPARABLE,
  NATIVE_PUBLICATION_REQUIRED: PRINT_NOT_PREPARABLE,
  BEDTIME_EDITION_PROMOTION_INCOMPLETE: PRINT_NOT_PREPARABLE,
  BEDTIME_EDITION_EXACT_FILES_REQUIRED:
    "Your book was approved from its pages on screen. To print it, open both files from your book’s page and order your printed copy there.",
  // The book changed since this page read it: the same request is refused until it is reloaded.
  LIVE_FAMILY_RENDER_SOURCE_STALE: PRINT_BOOK_CHANGED,
  IF_MATCH_REQUIRED: PRINT_BOOK_CHANGED,
  FORBIDDEN: "Only the book’s owner can prepare it for printing.",
  // A book variant's Book step (services/src/book-variants/print-delivery.ts, book-variant.ts).
  BOOK_VARIANT_NOT_PRINTABLE: SERVICE_WORDS,
  BOOK_VARIANT_NO_PRINTED_COPY: SERVICE_WORDS,
  BOOK_VARIANT_PRINT_OWNER_REQUIRED: SERVICE_WORDS,
  BOOK_VARIANT_EDITION_STALE: SERVICE_WORDS,
};

/**
 * Why print preparation was refused for good, in plain words, or null when trying again may work.
 * Only the listed permanent refusals count; every other answer leaves the retry in place.
 */
export function printPreparationRefusal(error: unknown): string | null {
  if (!(error instanceof ApiError) || !error.code) return null;
  const words = PERMANENT_PRINT_PREPARATION_REFUSALS[error.code];
  if (!words) return null;
  return words === SERVICE_WORDS
    ? friendlyRequestError(error, "This book can’t be prepared for printing right now.")
    : words;
}
